import { AnimationFactory } from "../types";

export const createChainLightningAnimation: AnimationFactory = ({
                                                                    ctx,
                                                                    canvas,
                                                                    startX,
                                                                    startY,
                                                                    endX,
                                                                    endY,
                                                                    startTime,
                                                                    scale
                                                                }) => {
    const SCALE = scale;

    const flightDuration = 420;
    const impactDuration = 260;
    const stormDuration = 620;
    const totalDuration = flightDuration + impactDuration + stormDuration;

    // 40% в кожну сторону від центру = дуже широкий удар
    const horizontalHalfRange = canvas.width * 0.4;

    const leftBound = Math.max(0, endX - horizontalHalfRange);
    const rightBound = Math.min(canvas.width, endX + horizontalHalfRange);

    type Spark = {
        angle: number;
        distance: number;
        size: number;
        speed: number;
    };

    const sparks: Spark[] = Array.from({ length: 55 }, () => ({
        angle: Math.random() * Math.PI * 2,
        distance: (20 + Math.random() * 90) * SCALE,
        size: (1.5 + Math.random() * 4) * SCALE,
        speed: 0.7 + Math.random() * 0.9,
    }));

    const drawLightningPath = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        options?: {
            segments?: number;
            amplitude?: number;
            forkChance?: number;
            forkLengthFactor?: number;
        }
    ) => {
        const segments = options?.segments ?? 20;
        const amplitude = (options?.amplitude ?? 22) * SCALE;
        const forkChance = options?.forkChance ?? 0.22;
        const forkLengthFactor = options?.forkLengthFactor ?? 0.45;

        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;

        const normalX = -dy / distance;
        const normalY = dx / distance;

        ctx.beginPath();
        ctx.moveTo(x1, y1);

        for (let i = 1; i < segments; i++) {
            const t = i / segments;

            const px = x1 + dx * t;
            const py = y1 + dy * t;

            const centerWeight = 1 - Math.abs(t - 0.5) * 2;
            const offset =
                (Math.random() - 0.5) *
                2 *
                amplitude *
                (0.45 + centerWeight * 0.9);

            const ox = px + normalX * offset;
            const oy = py + normalY * offset;

            ctx.lineTo(ox, oy);

            if (Math.random() < forkChance && t > 0.05 && t < 0.95) {
                const sign = Math.random() < 0.5 ? -1 : 1;
                const forkLen =
                    (distance / segments) *
                    (1 + Math.random() * 2) *
                    forkLengthFactor;

                const fx =
                    ox +
                    normalX * sign * forkLen +
                    (dx / distance) * forkLen * (0.1 + Math.random() * 0.35);
                const fy =
                    oy +
                    normalY * sign * forkLen +
                    (dy / distance) * forkLen * (0.1 + Math.random() * 0.35);

                ctx.moveTo(ox, oy);
                ctx.lineTo(fx, fy);
                ctx.moveTo(ox, oy);
            }
        }

        ctx.lineTo(x2, y2);
        ctx.stroke();
    };

    const drawOrb = (
        x: number,
        y: number,
        radius: number,
        alpha: number
    ) => {
        const g = ctx.createRadialGradient(x, y, 0, x, y, radius);

        g.addColorStop(0, `rgba(255,255,255,${alpha})`);
        g.addColorStop(0.15, `rgba(230,245,255,${alpha * 0.98})`);
        g.addColorStop(0.4, `rgba(150,220,255,${alpha * 0.9})`);
        g.addColorStop(0.7, `rgba(80,150,255,${alpha * 0.55})`);
        g.addColorStop(1, `rgba(40,100,255,0)`);

        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
    };

    const drawFlash = (
        x: number,
        y: number,
        radius: number,
        alpha: number
    ) => {
        const g = ctx.createRadialGradient(x, y, 0, x, y, radius);

        g.addColorStop(0, `rgba(255,255,255,${alpha})`);
        g.addColorStop(0.2, `rgba(220,240,255,${alpha * 0.95})`);
        g.addColorStop(0.45, `rgba(150,210,255,${alpha * 0.8})`);
        g.addColorStop(0.75, `rgba(70,120,255,${alpha * 0.4})`);
        g.addColorStop(1, `rgba(40,100,255,0)`);

        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
    };

    const drawStormBand = (
        x1: number,
        x2: number,
        y: number,
        alpha: number,
        intensity: number
    ) => {
        const glowWidth = 34 * SCALE * intensity;
        const coreWidth = 16 * SCALE * intensity;
        const whiteWidth = 7 * SCALE * intensity;

        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        // зовнішня товста аура
        ctx.lineWidth = glowWidth;
        ctx.strokeStyle = `rgba(70,130,255,${0.10 * alpha})`;
        drawLightningPath(x1, y, x2, y, {
            segments: 26,
            amplitude: 34,
            forkChance: 0.35,
            forkLengthFactor: 0.75,
        });

        // середній енергетичний шар
        ctx.lineWidth = coreWidth;
        ctx.strokeStyle = `rgba(110,190,255,${0.28 * alpha})`;
        drawLightningPath(x1, y, x2, y, {
            segments: 28,
            amplitude: 26,
            forkChance: 0.28,
            forkLengthFactor: 0.6,
        });

        // яскраве ядро
        ctx.lineWidth = whiteWidth;
        ctx.strokeStyle = `rgba(190,235,255,${0.85 * alpha})`;
        drawLightningPath(x1, y, x2, y, {
            segments: 30,
            amplitude: 16,
            forkChance: 0.20,
            forkLengthFactor: 0.4,
        });

        // білий сердечник
        ctx.lineWidth = 3.2 * SCALE * intensity;
        ctx.strokeStyle = `rgba(255,255,255,${0.98 * alpha})`;
        drawLightningPath(x1, y, x2, y, {
            segments: 32,
            amplitude: 10,
            forkChance: 0.10,
            forkLengthFactor: 0.25,
        });
    };

    const drawVerticalBursts = (
        centerX: number,
        centerY: number,
        alpha: number
    ) => {
        for (let i = 0; i < 6; i++) {
            const offsetX = (Math.random() - 0.5) * 90 * SCALE;
            const upLen = (40 + Math.random() * 90) * SCALE;
            const downLen = (40 + Math.random() * 90) * SCALE;

            ctx.lineWidth = 6 * SCALE;
            ctx.strokeStyle = `rgba(170,220,255,${0.35 * alpha})`;
            drawLightningPath(
                centerX + offsetX,
                centerY,
                centerX + offsetX,
                centerY - upLen,
                {
                    segments: 10,
                    amplitude: 18,
                    forkChance: 0.25,
                    forkLengthFactor: 0.45,
                }
            );

            ctx.lineWidth = 3 * SCALE;
            ctx.strokeStyle = `rgba(255,255,255,${0.7 * alpha})`;
            drawLightningPath(
                centerX + offsetX,
                centerY,
                centerX + offsetX,
                centerY + downLen,
                {
                    segments: 10,
                    amplitude: 12,
                    forkChance: 0.18,
                    forkLengthFactor: 0.35,
                }
            );
        }
    };

    const drawImpactSparks = (progress: number, alpha: number) => {
        for (const spark of sparks) {
            const dist = spark.distance * progress * spark.speed;
            const x = endX + Math.cos(spark.angle) * dist;
            const y = endY + Math.sin(spark.angle) * dist;

            ctx.fillStyle = `rgba(220,240,255,${alpha})`;
            ctx.beginPath();
            ctx.arc(x, y, spark.size * (1 - progress * 0.65), 0, Math.PI * 2);
            ctx.fill();
        }
    };

    const drawScreenFlash = (alpha: number) => {
        ctx.fillStyle = `rgba(180,220,255,${alpha})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    return (now: number) => {
        const elapsed = now - startTime;

        if (elapsed >= totalDuration) {
            return false;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const flicker =
            1 +
            Math.sin(elapsed * 0.045) * 0.12 +
            (Math.random() - 0.5) * 0.18;

        if (elapsed < flightDuration) {
            const progress = elapsed / flightDuration;

            const x = startX + (endX - startX) * progress;
            const y = startY + (endY - startY) * progress;

            // величезне ядро кулі
            drawOrb(x, y, 34 * SCALE, 0.95);
            drawOrb(x, y, 18 * SCALE, 1);

            // хвіст польоту
            ctx.lineCap = "round";
            ctx.lineJoin = "round";

            ctx.lineWidth = 18 * SCALE * flicker;
            ctx.strokeStyle = `rgba(70,130,255,0.12)`;
            drawLightningPath(startX, startY, x, y, {
                segments: 20,
                amplitude: 28,
                forkChance: 0.22,
                forkLengthFactor: 0.55,
            });

            ctx.lineWidth = 9 * SCALE * flicker;
            ctx.strokeStyle = `rgba(120,210,255,0.45)`;
            drawLightningPath(startX, startY, x, y, {
                segments: 22,
                amplitude: 18,
                forkChance: 0.18,
                forkLengthFactor: 0.4,
            });

            ctx.lineWidth = 4 * SCALE * flicker;
            ctx.strokeStyle = `rgba(255,255,255,0.95)`;
            drawLightningPath(startX, startY, x, y, {
                segments: 24,
                amplitude: 10,
                forkChance: 0.1,
                forkLengthFactor: 0.25,
            });

            return true;
        }

        if (elapsed < flightDuration + impactDuration) {
            const phaseElapsed = elapsed - flightDuration;
            const progress = phaseElapsed / impactDuration;

            const inv = 1 - progress;

            // майже екранний спалах
            drawScreenFlash(0.12 * inv);

            drawFlash(endX, endY, (90 + progress * 140) * SCALE, 0.95 * inv + 0.2);
            drawFlash(endX, endY, (45 + progress * 70) * SCALE, 1);
            drawOrb(endX, endY, (28 + progress * 20) * SCALE, 1);

            const currentLeft = endX - (endX - leftBound) * (0.25 + progress * 0.75);
            const currentRight = endX + (rightBound - endX) * (0.25 + progress * 0.75);

            // одразу декілька горизонтальних ліній шторму
            drawStormBand(currentLeft, currentRight, endY, 1, 1.3);
            drawStormBand(currentLeft, currentRight, endY - 18 * SCALE, 0.8, 1);
            drawStormBand(currentLeft, currentRight, endY + 18 * SCALE, 0.8, 1);

            drawVerticalBursts(endX, endY, 1 - progress * 0.25);
            drawImpactSparks(progress, 0.95 * inv + 0.2);

            return true;
        }

        const stormElapsed = elapsed - flightDuration - impactDuration;
        const progress = stormElapsed / stormDuration;
        const inv = 1 - progress;

        // слабкий загальний екранний імпульс
        drawScreenFlash(0.05 * inv);

        // гігантський центральний шторм
        drawFlash(endX, endY, (120 - progress * 30) * SCALE, 0.55 * inv);
        drawOrb(endX, endY, (36 - progress * 8) * SCALE, 0.85 * inv + 0.15);

        const jitterY = () => (Math.random() - 0.5) * 30 * SCALE;
        const leftX = leftBound;
        const rightX = rightBound;

        // багато горизонтальних шарів блискавки
        drawStormBand(leftX, rightX, endY + jitterY(), 1.0 * inv + 0.25, 1.5);
        drawStormBand(leftX, rightX, endY - 24 * SCALE + jitterY(), 0.9 * inv, 1.15);
        drawStormBand(leftX, rightX, endY + 24 * SCALE + jitterY(), 0.9 * inv, 1.15);
        drawStormBand(leftX, rightX, endY - 48 * SCALE + jitterY(), 0.65 * inv, 0.95);
        drawStormBand(leftX, rightX, endY + 48 * SCALE + jitterY(), 0.65 * inv, 0.95);

        drawVerticalBursts(endX, endY, 0.8 * inv + 0.2);

        // хаотичні додаткові дуги по всій зоні
        for (let i = 0; i < 18; i++) {
            const x1 = leftBound + Math.random() * (rightBound - leftBound);
            const y1 = endY + (Math.random() - 0.5) * 140 * SCALE;
            const x2 = x1 + (Math.random() - 0.5) * 160 * SCALE;
            const y2 = y1 + (Math.random() - 0.5) * 110 * SCALE;

            ctx.lineWidth = (2 + Math.random() * 4) * SCALE;
            ctx.strokeStyle = `rgba(170,220,255,${(0.2 + Math.random() * 0.35) * inv})`;
            drawLightningPath(x1, y1, x2, y2, {
                segments: 8 + Math.floor(Math.random() * 6),
                amplitude: 14 + Math.random() * 12,
                forkChance: 0.18,
                forkLengthFactor: 0.35,
            });

            ctx.lineWidth = (1 + Math.random() * 2) * SCALE;
            ctx.strokeStyle = `rgba(255,255,255,${(0.25 + Math.random() * 0.35) * inv})`;
            drawLightningPath(x1, y1, x2, y2, {
                segments: 7,
                amplitude: 8,
                forkChance: 0.08,
                forkLengthFactor: 0.2,
            });
        }

        // яскраві плазмові іскри
        for (let i = 0; i < 30; i++) {
            const x = leftBound + Math.random() * (rightBound - leftBound);
            const y = endY + (Math.random() - 0.5) * 150 * SCALE;
            const r = (1.8 + Math.random() * 4.5) * SCALE;

            ctx.fillStyle = `rgba(220,245,255,${(0.25 + Math.random() * 0.45) * inv})`;
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
        }

        return true;
    };
};